import { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { PRACTICE_FAMILY_INVITE_CODE, PRACTICE_OTP, getPracticeCustomerName, setPracticeCustomerName } from "./practiceMode";
import { resolveAccountRequest } from "./practiceAccountData";
import { resolveRegisterRequest } from "./practiceRegister";
import { practiceSupplierName } from "./practiceMerchant";
import {
  resolveOrderRequest,
  markPracticeSurveyDone,
  PRACTICE_SUPPLIER_ID,
  PRACTICE_ADDRESS_ID,
  PRACTICE_ADDRESS_FULL,
} from "./practiceOrder";

/**
 * 練習模式的「假後端」。
 * --------------------------------------------------------------------------
 * 練習模式啟用時，apiManager 的 request 攔截器會把每一筆請求改用這裡產生的 adapter，
 * 因此練習過程中「不會有任何真實後端流量」：
 *   - /app-api/line/*       → 依情境回傳腳本化結果（成功/錯誤）
 *   - address-normalization → 直接 reject，觸發既有的離線地址 fallback（縣市/區用本地資料）
 *   - 其餘端點             → 回傳良性空資料，避免練習中誤打到正式 API
 */

type PracticeAdapter = (config: InternalAxiosRequestConfig) => Promise<AxiosResponse>;

const ok = (data: unknown, config: InternalAxiosRequestConfig, status = 200): AxiosResponse => ({
  data,
  status,
  statusText: "OK",
  headers: {},
  config,
});

/** 產生 axios 會 reject 的錯誤物件；同時帶 top-level status 與 response.status，
 *  以相容各頁面不同的取值方式（例如 InvitedCode 讀 error?.status）。 */
const fail = (status: number, data: unknown, config: InternalAxiosRequestConfig): AxiosError => {
  const error = new Error(typeof (data as any)?.message === "string" ? (data as any).message : "練習模式錯誤") as AxiosError & {
    status?: number;
  };
  error.name = "AxiosError";
  error.isAxiosError = true;
  error.config = config;
  error.status = status;
  error.response = {
    data,
    status,
    statusText: "ERROR",
    headers: {},
    config,
  } as AxiosResponse;
  return error;
};

const url = (config: InternalAxiosRequestConfig) => config.url || "";
const matches = (config: InternalAxiosRequestConfig, path: string) => url(config).includes(path);
const body = (config: InternalAxiosRequestConfig): Record<string, any> => {
  try {
    return typeof config.data === "string" ? JSON.parse(config.data) : config.data || {};
  } catch {
    return {};
  }
};

/** 新增送達地址用的遞增 id（練習沙盒；createCustomerAddress 要求 customer_address_id 為數字）。 */
let practiceAddressSeq = 0;

/** 練習用的家庭帳戶 context（給帳號列表/詳情/邀請頁顯示；角色設 owner 才能產生邀請碼）。 */
const practiceContext = () => ({
  context_id: 9001,
  context_name: "練習家庭帳戶",
  role: "owner",
  is_payer: true,
  member_count: 2,
  max_members: 6,
  // supplier_id / 地址與下單情境一致，避免 IdentitySwitchMenu 同步後對不到 bootstrap 供應商。
  primary_supplier: { supplier_name: practiceSupplierName(), supplier_id: PRACTICE_SUPPLIER_ID, is_open_now: true },
  choose_address: { customer_address_id: PRACTICE_ADDRESS_ID, full_address: PRACTICE_ADDRESS_FULL },
  address: [{ customer_address_id: PRACTICE_ADDRESS_ID, full_address: PRACTICE_ADDRESS_FULL }],
  members: [
    { customer_name: "練習負責人", role: "owner" },
    { customer_name: "練習成員", role: "member" },
  ],
});

/** 練習用的瓦斯行 / 客戶資料（authentication-validation 回傳）。 */
const practiceGasData = () => ({
  supplier_id: "DEMO-001",
  supplier_name: "練習示範瓦斯行",
  supplier: { show_invoice: true },
  verifiedTicket: "PRACTICE_VERIFIED_TICKET",
  customerInfo: {
    mainPhone: "",
    invoiceCarrier: "",
    taxIdNumber: "",
    // 留空 → 基本資料頁不會自動呼叫地址切分 API，由客服練習手動填入地址。
    deliveryAddressList: [],
  },
});

/**
 * 依目前情境，為這筆請求回傳對應的假回應 adapter。
 * 練習模式啟用時，所有請求都會走這裡（保證零真實流量）。
 */
export const resolvePracticeAdapter = (config: InternalAxiosRequestConfig): PracticeAdapter => {
  return () => {
    // --- 地址正規化：reject 以觸發既有離線 fallback（本地縣市/區資料）---------
    if (matches(config, "address-normalization-api")) {
      return Promise.reject(fail(503, { message: "practice offline address" }, config));
    }

    // === 正常綁定（四步驟）相關端點 ==========================================
    // 進站 resume 檢查：一律回「無既有綁定」，讓客服從頭手動輸入。
    if (matches(config, "/app-api/line/authentication-code-check")) {
      return Promise.resolve(ok({ data: { hasBinding: false, authenticationCode: "" } }, config));
    }

    // 客戶序號驗證（永遠成功）
    if (matches(config, "/app-api/line/authentication-validation")) {
      return Promise.resolve(ok({ data: practiceGasData() }, config));
    }

    // 發送手機 OTP（永遠成功）。回冷卻秒數，讓「重新傳送／改用語音驗證」的倒數
    // 在練習流程裡也跟正式環境一致（語音 90s、簡訊 60s）。
    if (matches(config, "/app-api/line/send-phone-otp")) {
      const { channel } = body(config) as { channel?: string };
      return Promise.resolve(ok({ data: { success: true, resendAvailableInSeconds: channel === "voice" ? 90 : 60 } }, config));
    }

    // 驗證手機 OTP：只有輸入練習用驗證碼才正確。
    if (matches(config, "/app-api/line/verify-phone-otp")) {
      const { otpCode } = body(config);
      if (otpCode !== PRACTICE_OTP) {
        return Promise.reject(fail(400, { message: "驗證碼錯誤" }, config));
      }
      return Promise.resolve(ok({ data: { phoneVerifiedTicket: "PRACTICE_PHONE_TICKET" } }, config));
    }

    // 完成註冊 → 用 redirectUrl 導回練習完成頁，留在沙盒內。
    if (matches(config, "/app-api/line/register")) {
      return Promise.resolve(ok({ data: { redirectUrl: "/practice?done=normal" } }, config));
    }

    // onboarding 綁定/註冊家族（/app-api/register/* + line/login）：走 demo 供應商時少數分支
    // （推薦附近/未綁定登記/session 刷新）仍會打真後端，全部在此 mock 成 demoData 一致的結果。
    const registerResult = resolveRegisterRequest(config);
    if (registerResult) {
      return Promise.resolve(ok(registerResult, config));
    }

    // === 家庭帳戶相關端點 ====================================================
    // 取得邀請碼（邀請成員頁，限 owner）— 須在通用 contexts 判斷之前。
    if (matches(config, "/invite-code")) {
      return Promise.resolve(ok({ data: { invite_code: PRACTICE_FAMILY_INVITE_CODE, expires_at: "2099-12-31T23:59:59Z" } }, config));
    }

    // 加入家庭（輸入邀請碼）
    if (matches(config, "/app-api/customer/contexts/join")) {
      return Promise.resolve(ok({ data: { context_id: 9001, status: "joined", message: "" } }, config));
    }

    // 更新客戶姓名（帳號頁編輯姓名）→ 存進練習沙盒，之後 /customer/home 會讀到最新值。
    if (matches(config, "/app-api/customer/profile")) {
      const b = body(config);
      if (typeof b.customer_name === "string" && b.customer_name.trim()) setPracticeCustomerName(b.customer_name.trim());
      return Promise.resolve(ok({ data: { customer_name: getPracticeCustomerName() } }, config));
    }

    // 新增送達地址（POST /app-api/delivery/address）。沒有這支時會掉進最後的空物件 fallthrough，
    // createCustomerAddress 取不到數字 customer_address_id 就丟「回傳格式異常」→ 畫面顯示
    // 「新增地址失敗，請稍後再試」。回遞增 id 讓練習可以連續新增成功。
    // 註：/delivery/address/location/ 是另一支（由 resolveOrderRequest 處理），此處排除。
    if (
      matches(config, "/app-api/delivery/address") &&
      !matches(config, "/delivery/address/location/") &&
      (config.method || "").toLowerCase() === "post"
    ) {
      practiceAddressSeq += 1;
      const id = 90000 + practiceAddressSeq;
      return Promise.resolve(
        ok(
          {
            data: {
              customerAddressResult: { customer_address_id: id },
              addressBindingResult: { address_binding_id: id, customer_address_id: id, address_id: id },
            },
          },
          config,
        ),
      );
    }

    // 首頁叫桶週期預測的回饋／暫停（HomePage 的預測卡片會打這兩支）→ 回成功即可。
    if (matches(config, "/app-api/customer/cycle-feedback") || matches(config, "/app-api/customer/cycle-pause")) {
      return Promise.resolve(ok({ data: { success: true } }, config));
    }

    // 用氣習慣問卷送出 → 回成功（練習沙盒；預估值由前端 surveyStorage 依答案本地計算）。
    // 同時把沙盒旗標打開，之後 /customer/home 的 survey_prompt 就回 needed:false，
    // 否則填完後重抓首頁資料，問卷卡會立刻又長回來。
    if (matches(config, "/app-api/customer/gas-survey")) {
      markPracticeSurveyDone();
      return Promise.resolve(
        ok(
          {
            data: {
              survey_id: "PRACTICE-SURVEY-001",
              survey_completed_at: "2099-01-01T00:00:00Z",
              prediction_source: "survey",
              run_date: "2099-01-01",
              home_gas_status: null,
            },
          },
          config,
        ),
      );
    }

    // 帳號讀取類端點（付款方式、交易/對帳紀錄、context 詳情）— 須在 contexts 列表判斷之前，
    // 以攔截 contexts/:id 詳情；也在下單 mock 之前，以攔截 platform-orders/account-*。
    const accountResult = resolveAccountRequest(config);
    if (accountResult) {
      return Promise.resolve(ok(accountResult, config));
    }

    // 帳號列表：回傳一個練習家庭帳戶（owner）。Account 頁讀 res.data.data.contexts。
    if (matches(config, "/app-api/customer/contexts")) {
      return Promise.resolve(ok({ data: { contexts: [practiceContext()], default_context_id: 9001 } }, config));
    }

    // --- 下單流程（gasShop / kiosk 共用的草稿/訂單端點）-----------------------
    // resolveOrderRequest 回傳的就是 response body（已含外層 { data: ... }），直接當 body。
    const orderResult = resolveOrderRequest(config);
    if (orderResult) {
      return Promise.resolve(ok(orderResult, config));
    }

    // --- 其餘端點：回良性空物件（非 null），避免頁面讀 data.items/data.xxx 時在 null 上爆掉 ---
    return Promise.resolve(ok({ data: {}, message: "練習模式" }, config));
  };
};
